/**
 * React adapter — used by espotz-webapp and espotz-mobile-app.
 *
 * Exports `useFeatureFlag(name)` with the SAME return shape as the existing
 * webapp hook (isEnabled, isLoading, error, toggleFlag, setFlag) so callers
 * don't need to change.
 *
 * Singleton pattern: one FeatureFlagClient per app, created at boot and
 * shared across all hook calls. React doesn't need a Provider for this —
 * the client is imported as a module singleton.
 */

import { useCallback, useEffect, useState } from 'react';
import { FeatureFlagClient } from './client';

let singleton: FeatureFlagClient | null = null;

/**
 * Register the client at app boot. Call this ONCE, before any component
 * that uses useFeatureFlag renders. Idempotent — subsequent calls are
 * no-ops (the first registration wins).
 *
 * @example
 *   // src/main.tsx (webapp) or src/App.tsx (RN)
 *   import { FeatureFlagClient } from '@/feature-flag/client';
 *   import { registerFeatureFlagClient } from '@/feature-flag/react';
 *   import defaults from '@/feature-flag/defaults.json';
 *
 *   registerFeatureFlagClient(new FeatureFlagClient({
 *     url: import.meta.env.VITE_FF_URL,
 *     defaults,
 *     overrides: readLocalOverrides(),  // see README
 *   }));
 */
export function registerFeatureFlagClient(client: FeatureFlagClient): void {
  if (singleton !== null) return;
  singleton = client;
  client.init();
}

/**
 * @returns the registered client, or throws if registerFeatureFlagClient
 * hasn't been called yet. Useful for non-hook contexts (services, utilities).
 */
export function getFeatureFlagClient(): FeatureFlagClient {
  if (singleton === null) {
    throw new Error(
      'FeatureFlagClient not registered. Call registerFeatureFlagClient() at app boot.',
    );
  }
  return singleton;
}

/**
 * False until the client's first CDN fetch has settled (succeeded or failed); true at once when
 * there is no CDN URL (local dev). Flags read false before then on deployed builds, so a page that
 * redirects when a flag is off must wait for this, or a hard refresh bounces users the flag allows.
 */
export function useFeatureFlagsReady(): boolean {
  const client = getFeatureFlagClient();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    void client.init().then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [client]);

  return ready;
}

export interface UseFeatureFlagReturn {
  /** Current effective value. False if unknown or not yet loaded. */
  isEnabled: boolean;
  /** True only on the very first render before init completes. */
  isLoading: boolean;
  /** Last fetch error message, if any. Null on success. */
  error: string | null;
  /** Flip the local override of this flag. Useful for DevTools / QA. */
  toggleFlag: () => void;
  /** Force-set a local override of this flag. */
  setFlag: (value: boolean) => void;
}

/**
 * Read a feature flag inside a React component. Re-renders when:
 *   - The CDN-fetched flags refresh (every ~60s)
 *   - Any caller toggles this flag via setOverride / setFlag / toggleFlag
 *
 * @example
 *   const { isEnabled } = useFeatureFlag('academy');
 *   return isEnabled ? <NewShareModal /> : <LegacyShareModal />;
 */
export function useFeatureFlag(flagName: string): UseFeatureFlagReturn {
  const client = getFeatureFlagClient();
  const [, forceRender] = useState(0);
  // Bundled defaults are always available synchronously, so isLoading is
  // false from the start. The hook re-renders when CDN fetches succeed
  // (via subscribe) so eventual-consistent CDN values still apply.
  const isLoading = false;
  const error: string | null = null;

  useEffect(() => {
    const unsubscribe = client.subscribe(() => {
      forceRender((n: number) => n + 1);
    });
    // Idempotent — only the first call starts the background polling.
    void client.init();
    return unsubscribe;
  }, [client]);

  const setFlag = useCallback(
    (value: boolean) => {
      client.setOverride(flagName, value);
    },
    [client, flagName],
  );

  const toggleFlag = useCallback(() => {
    client.setOverride(flagName, !client.isEnabled(flagName));
  }, [client, flagName]);

  return {
    isEnabled: client.isEnabled(flagName),
    isLoading,
    error,
    toggleFlag,
    setFlag,
  };
}

/** Non-hook utilities — same shape as the existing webapp helpers. */
export function getFeatureFlagValue(flagName: string): boolean {
  return getFeatureFlagClient().isEnabled(flagName);
}

export function hasFeatureFlag(flagName: string): boolean {
  return flagName in getFeatureFlagClient().getAllFlags();
}

export function getAllFeatureFlags(): Readonly<Record<string, boolean>> {
  return getFeatureFlagClient().getAllFlags();
}

/**
 * Wires window.__ff for QA / DevTools / support. Safe to install in every
 * environment (including production) — flag values are public (CDN serves
 * them) and overrides are SESSION-ONLY (in-memory; cleared on refresh).
 *
 * No localStorage persistence: a stray `__ff.set()` from a console paste
 * or an old debugging session can't silently override the CDN value for
 * that user forever. For persistent local overrides, edit env.json.
 *
 * @example  window.__ff.set('academy', true)   // until next reload
 * @example  window.__ff.clear('academy')        // back to CDN value
 * @example  window.__ff.list()                              // snapshot all flags
 */
export function installDevToolsHook(): void {
  if (typeof window === 'undefined') return;
  const client = getFeatureFlagClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__ff = {
    set: (name: string, value: boolean) => client.setOverride(name, value),
    clear: (name: string) => client.setOverride(name, null),
    list: () => client.getAllFlags(),
    /** Force a CDN refresh. Resolves once the fetch completes. */
    refresh: () => client.refresh(),
  };
}
